import React, { memo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import { StencilCDNImage } from '../Common/StencilImage'
import { CatalogueOverviewProps } from '@/components/catalogue/types'
import { CurrencyToggle } from '@/components/catalogue/CurrencyToggle'
import { PriceTicker } from '@/components/catalogue/PriceTicker'
import Text from '@/stories/components/Common/NewText'
import { getUsStockLivePriceUrl } from '@/root/api/apiConstants'
import SubHeaderList from '@/stories/components/DetailPage/SubHeaderList'
import { BELL_ICON } from '@/root/constants/image-links'
import { getDateMonthYear } from '@/root/utils/common/date/get-date-format'

const USStocksChart = memo(
  dynamic(() => import('@/components/catalogue/CatalogueChart')),
)

const CatalogueOverview = (props: CatalogueOverviewProps) => {
  const {
    short_name = '',
    name = '',
    symbol = '',
    logo = '',
    live_price = 0,
    percent_change = 0,
    day_change = 0,
    metadata,
    isMobile,
    meta_info = {},
    isGraphDisabled = false,
    last_updated,
    linked_collections,
    exchange,
    currency,
    onCurrencyToggle,
    ticker,
    exchange_metadata,
    isMarketOpen,
    token,
    delistInfo,
    showLiveMarket,
    ...rest
  } = props

  const router = useRouter()

  const list = [
    ...(exchange_metadata?.map(item => ({
      label: `${item.exchange}: ${item.stock_symbol}`,
    })) ?? []),
    ...(symbol && !exchange_metadata?.length
      ? [{ label: `${exchange ? `${exchange}` : 'Quote'}: ${symbol}` }]
      : []),
    ...(linked_collections?.map(item => ({
      label: item.title,
      url: item.cta,
    })) ?? []),
  ]

  return (
    <div>
      <section className="flex gap-2 md:items-center md:gap-3">
        {logo && (
          <StencilCDNImage
            className="h-[42px] min-w-[42px] rounded-full"
            height={42}
            width={42}
            src={logo}
            alt={`${name} share price logo`}
            objectFit="contain"
          />
        )}
        <div className="flex flex-col gap-y-1">
          <Text appearance="heading2" tag="h1">
            {`${short_name ?? name} Share Price`}
          </Text>
          {!!list?.length && <SubHeaderList list={list} />}
        </div>
      </section>

      <div className="my-4 flex items-end justify-between border-b pb-2 md:my-5">
        <PriceTicker
          live_price={Number(live_price)}
          day_change={Number(day_change)}
          day_change_percentage={Number(percent_change)}
          currencySymbol={meta_info.currency_symbol ?? '$'}
          ticker={ticker}
          currency={currency ?? 'USD'}
          apiUrl={getUsStockLivePriceUrl}
          last_updated={last_updated}
          isMarketOpen={isMarketOpen}
          showLiveMarket={showLiveMarket}
        />
        <div className="flex flex-row items-center gap-x-4">
          <CurrencyToggle
            currency={currency}
            onCurrencyToggle={onCurrencyToggle}
          />
          {!isMobile && ticker && !token && (
            <button
              className={classNames(
                'bg-brand-green-dark ml-auto rounded-full px-8 py-2 text-white',
                { 'opacity-30': !delistInfo?.is_active },
              )}
              onClick={() => {
                void router.push(`/login?return_to=${router.asPath}`)
              }}
              disabled={!delistInfo?.is_active}
            >
              <Text appearance="body2" tag="span">
                Invest in {ticker}
              </Text>
            </button>
          )}
        </div>
      </div>

      {!delistInfo?.is_active && (
        <div className="bg-red-tertiary flex flex-row gap-y-2.5 rounded-md p-4">
          <div className="mr-2 flex w-6 items-center justify-center md:w-4">
            <StencilCDNImage
              alt="Bell Icon"
              src={BELL_ICON}
              width={16}
              height={16}
              className="flex h-4 w-4"
            />
          </div>
          <Text appearance="subtitle1" addClassName="text-red">
            The stock has been delisted from the stock exchange{' '}
            {delistInfo?.last_active_date &&
              `on ${getDateMonthYear(delistInfo?.last_active_date)}`}
          </Text>
        </div>
      )}

      {isGraphDisabled ? null : (
        <USStocksChart
          name={name}
          {...rest}
          meta_info={meta_info}
          isMobile={isMobile}
        />
      )}
    </div>
  )
}

export default CatalogueOverview
